// Build Line - the admin tab shell.
//
// Sub-views on one tab (design 10). This shell owns navigation, the board's
// search/status filters, and the toast. Each view fetches its own data,
// because their cadences differ - a running order moves every few seconds, the profile list
// never does - and a shared fetcher here would either over-poll the quiet ones
// or under-poll the live one. Same division of labour as the Px3 tab.
//
// SECURITY: nothing in this tree can render a borrowed credential. The order
// rows carry only `has_*` booleans and `secrets_purged_at`, and the enrollment's
// key fields are write-only - typed in, sent up, never read back.

import { useCallback, useEffect, useRef, useState } from 'react';

import './BuildLine.css';
import { Enroll } from './Enroll';
import { OrderDetail } from './OrderDetail';
import { Orders } from './Orders';
import { Profiles } from './Profiles';
import { Runs } from './Runs';
import { WorkerWarnings } from './WorkerWarnings';
import type { BuildOrderFilters } from './buildApi';

const TOAST_MS = 8000;

export type BuildToastKind = 'success' | 'error' | 'info' | 'warn';

/** The signature every sub-view gets for talking back to the operator. */
export type BuildShowToast = (kind: BuildToastKind, message: string) => void;

type BuildView = 'orders' | 'profiles' | 'enroll' | 'runs';

const VIEWS: { id: BuildView; label: string }[] = [
  { id: 'orders', label: 'Orders' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'enroll', label: 'Enroll' },
  { id: 'runs', label: 'Runs' },
];

interface Toast {
  type: BuildToastKind;
  message: string;
}

export function BuildLine(): JSX.Element {
  const [view, setView] = useState<BuildView>('orders');
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);
  const [orderFilters, setOrderFilters] = useState<BuildOrderFilters>({
    search: '', status: 'all', exclude_aborted: true,
  });
  const [toast, setToast] = useState<Toast | null>(null);

  /**
   * One toast at a time, one timer at a time.
   *
   * The previous timer has to be CANCELLED, not merely outlived: a toast raised
   * near the end of an earlier toast's window is otherwise wiped by that earlier
   * timer firing. Several messages here exist only as a toast (the panels a
   * launch allocated, the shortfall it could not place), so a stray timer would
   * swallow the only report of what a click did. Cleared on unmount too, so the
   * callback cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback<BuildShowToast>((type, message) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const openOrder = useCallback((orderId: number) => {
    setView('orders');
    setOpenOrderId(orderId);
  }, []);

  return (
    <div className="bl">
      <div className="bl-scroll">
        <header className="bl-header">
          <h1 className="bl-title">Build Line</h1>
        </header>

        <nav className="bl-subnav">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`bl-subnav-item ${view === v.id ? 'active' : ''}`}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </nav>

        {toast && <div className={`bl-toast bl-toast--${toast.type}`}>{toast.message}</div>}
        <WorkerWarnings />

        {/* The board and one order are the same sub-view, not two tabs: an
            operator drills into an order and comes back, and a launch drops them
            straight onto the order it just started (Enroll's onLaunched). */}
        {view === 'orders' &&
          (openOrderId === null ? (
            <Orders onOpen={setOpenOrderId} showToast={showToast}
              filters={orderFilters} onFiltersChange={setOrderFilters} />
          ) : (
            <OrderDetail
              orderId={openOrderId}
              onBack={() => setOpenOrderId(null)}
              showToast={showToast}
            />
          ))}
        {view === 'profiles' && <Profiles showToast={showToast} />}
        {view === 'enroll' && <Enroll showToast={showToast} onLaunched={openOrder} />}
        {view === 'runs' && <Runs showToast={showToast} />}
      </div>
    </div>
  );
}
